import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { soundManager } from '../utils/soundManager';
import { supportsHaptics } from '../utils/haptics';
import { useTooltips } from '../context/TooltipContext';

interface SoundControlsProps {
  className?: string;
}

const ControlsContainer = styled.div`
  background: rgba(30, 30, 40, 0.92); /* Mörkare och mindre transparent */
  backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 16px;
  color: white;
  min-width: 200px;
`;

const Title = styled.h3`
  margin: 0 0 16px 0;
  font-size: 16px;
  text-align: center;
  color: white;
`;

const ControlGroup = styled.div`
  margin-bottom: 12px;
`;

const Label = styled.label`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  margin-bottom: 8px;
  cursor: pointer;
`;

const Toggle = styled.input.attrs({ type: 'checkbox' })`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const Slider = styled.input.attrs({ type: 'range' })`
  width: 100%;
  height: 4px;
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.3);
  outline: none;
  cursor: pointer;
  
  &::-webkit-slider-thumb {
    appearance: none;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: white;
    cursor: pointer;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  }
  
  &::-moz-range-thumb {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: white;
    cursor: pointer;
    border: none;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  }
`;

const VolumeLabel = styled.div`
  font-size: 12px;
  opacity: 0.8;
  margin-top: 4px;
`;

const Select = styled.select`
  width: 100%;
  padding: 6px 8px;
  border-radius: 6px;
  border: 1px solid rgba(255,255,255,0.3);
  background: rgba(255,255,255,0.2);
  color: white;
`;

const TestButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 12px;
  cursor: pointer;
  margin-top: 8px;
  width: 100%;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const SoundControls: React.FC<SoundControlsProps> = ({ className }) => {
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [musicEnabled, setMusicEnabled] = useState(true);
  const [effectsEnabled, setEffectsEnabled] = useState(true);
  const [musicVolume, setMusicVolume] = useState(0.4);
  const [trackId, setTrackId] = useState('');
  const [effectsVolume, setEffectsVolume] = useState(0.7);
  const [hapticsEnabled, setHapticsEnabled] = useState(true);
  const [isMusicPlaying, setIsMusicPlaying] = useState(false);
  const { enabled: tooltipsEnabled, setEnabled: setTooltipsEnabled } = useTooltips();

  useEffect(() => {
    // Ladda inställningar från soundManager
    const settings = soundManager.getSettings();
    setSoundEnabled(settings.enabled);
    setMusicEnabled(!!settings.musicEnabled);
    setMusicVolume(settings.musicVolume ?? 0.4);
    setEffectsVolume(settings.effectsVolume ?? settings.volume ?? 0.7);
    setEffectsEnabled(settings.effectsEnabled !== false);
    setHapticsEnabled(settings.hapticsEnabled !== false);
    // Try to start music if enabled
    if (settings.enabled && settings.musicEnabled) {
      soundManager.playMusic();
    }
    setTrackId(soundManager.getCurrentTrack().id);

    const el = (soundManager as any).musicEl as HTMLAudioElement | null;
    if (el) setIsMusicPlaying(!el.paused);
    const handler = () => {
      const el2 = (soundManager as any).musicEl as HTMLAudioElement | null;
      if (el2) setIsMusicPlaying(!el2.paused);
    };
    document.addEventListener('visibilitychange', handler);
    return () => {
      document.removeEventListener('visibilitychange', handler);
    };
  }, []);

  const handleSoundEnabledChange = (enabled: boolean) => {
    setSoundEnabled(enabled);
    soundManager.setEnabled(enabled);
  };


  const handleEffectsVolumeChange = (newVolume: number) => {
    setEffectsVolume(newVolume);
    soundManager.setEffectsVolume(newVolume);
  };
  const handleEffectsEnabledChange = (enabled: boolean) => {
    setEffectsEnabled(enabled);
    (soundManager as any).setEffectsEnabled(enabled);
  };

  const handleHapticsChange = (enabled: boolean) => {
    setHapticsEnabled(enabled);
    soundManager.setHapticsEnabled(enabled);
  };

  const handleMusicEnabledChange = (enabled: boolean) => {
    setMusicEnabled(enabled);
    soundManager.setMusicEnabled(enabled);
  };

  const handleMusicVolumeChange = (newVolume: number) => {
    setMusicVolume(newVolume);
    soundManager.setMusicVolume(newVolume);
  };

  const handleTrackChange = async (id: string) => {
    setTrackId(id);
    await soundManager.setTrack(id);
  };

  const handleTestSound = () => {
    soundManager.playDiceRoll();
  };

  const togglePlayPause = async () => {
    const el = (soundManager as any).musicEl as HTMLAudioElement | null;
    if (!el) {
      await soundManager.playMusic();
      const newEl = (soundManager as any).musicEl as HTMLAudioElement | null;
      if (newEl) setIsMusicPlaying(!newEl.paused);
      return;
    }
    if (el.paused) {
      try { await el.play(); setIsMusicPlaying(true); } catch {}
    } else {
      try { el.pause(); setIsMusicPlaying(false); } catch {}
    }
  };

  return (
    <ControlsContainer className={className}>
      <Title>🔊 Ljudinställningar</Title>
      
      <ControlGroup>
        <Label>
          Ljud aktiverat
          <Toggle
            checked={soundEnabled}
            onChange={(e) => handleSoundEnabledChange(e.target.checked)}
          />
        </Label>
      </ControlGroup>

      <ControlGroup>
        <Label>
          Bakgrundsmusik
          <Toggle
            checked={musicEnabled}
            onChange={(e) => handleMusicEnabledChange(e.target.checked)}
          />
        </Label>
      </ControlGroup>

      <ControlGroup>
        <Label>
          Ljudeffekter
          <Toggle
            checked={effectsEnabled}
            onChange={(e) => handleEffectsEnabledChange(e.target.checked)}
            disabled={!soundEnabled}
          />
        </Label>
      </ControlGroup>

      <ControlGroup>
        <Label>
          Haptik { !supportsHaptics && <span style={{fontSize:'11px',opacity:0.7}}> (Ej tillgänglig på iOS)</span> }
          <Toggle
            checked={hapticsEnabled}
            onChange={(e) => handleHapticsChange(e.target.checked)}
          />
        </Label>
      </ControlGroup>
      <ControlGroup>
        <Label>
          Tooltips
          <Toggle
            checked={tooltipsEnabled}
            onChange={(e) => setTooltipsEnabled(e.target.checked)}
          />
        </Label>
      </ControlGroup>
      {!supportsHaptics && (
        <div style={{marginTop:-4, marginBottom:12, fontSize:11, lineHeight:1.3, opacity:0.75}}>
          Apple tillåter inte Vibration API i Safari eller PWA:er. Fungerar på Android och desktop-Chrome.
        </div>
      )}

      <ControlGroup>
        <div style={{ marginBottom: '8px', fontSize: '14px' }}>
          Spellista
        </div>
        <Select
          value={trackId}
          onChange={(e) => handleTrackChange(e.target.value)}
          disabled={!soundEnabled || !musicEnabled}
        >
          {soundManager.getPlaylist().map(t => (
            <option key={t.id} value={t.id}>{t.title}</option>
          ))}
        </Select>
      </ControlGroup>

      <ControlGroup>
        <div style={{ marginBottom: '8px', fontSize: '14px' }}>
          Effekter
        </div>
        <Slider
          min="0"
          max="1"
          step="0.1"
          value={effectsVolume}
          onChange={(e) => handleEffectsVolumeChange(parseFloat(e.target.value))}
          disabled={!soundEnabled}
        />
        <VolumeLabel>
          {Math.round(effectsVolume * 100)}%
        </VolumeLabel>
      </ControlGroup>

      <ControlGroup>
        <div style={{ marginBottom: '8px', fontSize: '14px' }}>
          Musikvolym
        </div>
        <Slider
          min="0"
          max="1"
          step="0.1"
          value={musicVolume}
          onChange={(e) => handleMusicVolumeChange(parseFloat(e.target.value))}
          disabled={!soundEnabled || !musicEnabled}
        />
        <VolumeLabel>
          {Math.round(musicVolume * 100)}%
        </VolumeLabel>
      </ControlGroup>

      <ControlGroup>
        <TestButton
          onClick={togglePlayPause}
          disabled={!soundEnabled || !musicEnabled}
        >
          {isMusicPlaying ? '⏸️ Pausa musik' : '▶️ Spela musik'}
        </TestButton>
      </ControlGroup>

      <ControlGroup>
        <TestButton 
          onClick={handleTestSound}
          disabled={!soundEnabled}
        >
          🎲 Testa ljud
        </TestButton>
      </ControlGroup>
    </ControlsContainer>
  );
};

export default SoundControls;
